import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import Script from 'next/script';

import Notification from '../components/business-notification/business-notification';
import { BusinessLayoutContextWrapper, BusinessTheme, FooterItems } from '../contexts/LayoutContext';
import { NotificationCtxWrapper } from '../contexts/NotificationContext';
import { ALL_BUSINESS_PREFIXES, Locale, stripLocaleFromURL } from '../helpers/LangHelper';
import { getBusinessHubMenu, getCommonPageDetails } from '../shared/commonPageDetails';
import { GTM_ID } from '../shared/env';
import { BusinessHubMenuItemType } from '../types/BusinessIndustries';
import { Category } from '../types/Category';

import '../styles/globals.scss';

interface Props extends AppProps {
    footerItems: FooterItems;
    categories: Category[];
    businessHubMenuData: BusinessHubMenuItemType[];
}

function BusinessHome({ Component, pageProps, footerItems, categories, businessHubMenuData }: Props) {
    let theme = pageProps?.page?.data?.layout;
    if (pageProps?.model === 'business-hub') {
        theme = BusinessTheme.BUSINESS_HUB;
    }

    return (
        <>
            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
                {!!pageProps.canonicalUrl && <link rel="canonical" href={pageProps.canonicalUrl} />}
            </Head>

            <NotificationCtxWrapper>
                <Notification />
                <BusinessLayoutContextWrapper footerItems={footerItems} categories={categories} businessHubMenuData={businessHubMenuData} theme={theme}>
                    <Component {...pageProps} />
                </BusinessLayoutContextWrapper>
            </NotificationCtxWrapper>
            <Script
                id="ga-script"
                dangerouslySetInnerHTML={{
                    __html: `
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');`,
                }}
            />
        </>
    );
}

BusinessHome.getInitialProps = async (context: AppContext) => {
    const pageProps = await App.getInitialProps(context); // Retrieves page's `getInitialProps`

    let querySlug = context.router.query.slug ?? '';
    if (context.router.asPath.startsWith(`/${Locale.USA}`)) {
        querySlug = [Locale.USA, ...(Array.isArray(querySlug) ? querySlug : [querySlug])];
    }

    let pageSlug = stripLocaleFromURL(Array.isArray(querySlug) ? querySlug.join('/') : querySlug);
    let lang: Locale = (querySlug as any)?.[0];

    if (ALL_BUSINESS_PREFIXES.includes(pageSlug.replace(/\//g, ''))) {
        pageSlug = '/business/resources';
    }

    let overrideLang = lang;
    if (lang === Locale.CanadaEnglish) {
        overrideLang = Locale.USA;
    }
    const { footerItems, categories } = await getCommonPageDetails(pageSlug, overrideLang);
    const { menuData } = await getBusinessHubMenu(overrideLang);

    return {
        ...pageProps,
        footerItems,
        categories,
        businessHubMenuData: menuData,
    };
};

export default BusinessHome;
